import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export default function ProtectedRoute({ children, requireAdmin = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthLoading } = useAuth();

  useEffect(() => {
    // Wait until auth state is resolved
    if (isAuthLoading) return;

    // If user data was passed from AuthCallback, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Verify session via backend (relies on cookie session)
    const checkAuth = async () => {
      try {
        const response = await authAPI.getMe();
        const userData = response.data;

        if (requireAdmin && !['super_admin', 'admin'].includes(userData.role)) {
          navigate('/dashboard', { replace: true });
          return;
        }

        setUser(userData);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state, requireAdmin, isAuthLoading]);

  // Loading state
  if (isAuthenticated === null || isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Pass user to children
  return typeof children === 'function' ? children({ user }) : children;
}
